import React, { useState } from "react";
import { useTitle } from "../../hooks/useTitle";
import * as Yup from "yup";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import {
	Avatar,
	Button,
	CssBaseline,
	TextField,
	Grid,
	Box,
	Typography,
	Container,
	InputAdornment,
	IconButton,
	Link,
	FormControlLabel,
    Checkbox,
	FormControl,
	FormHelperText,
	Collapse,
    Paper
} from '@material-ui/core';

import { LockOutlined, Visibility, VisibilityOff, Info} from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import LogoUploader from "../../components/LogoUploader";
import LocationSelector from "../../components/LocationSelector";

const useStyles = makeStyles(theme => ({
	paper: {
		marginTop: theme.spacing(8),
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
    padding: "20px 0"
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(3),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
  logoContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
	alertBox: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    backgroundColor: '#fff9c4',
    color: '#000',
    border: '1px solid #fdd835',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
}));

const SignUpSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "¡Demasiado corto!")
    .max(50, "¡Demasiado largo!")
    .required("El nombre de usuario es obligatorio"),
  companyName: Yup.string()
    .min(2, "¡Demasiado corto!")
    .max(100, "¡Demasiado largo!")
    .required("El nombre de compañia es obligatorio"),
  country: Yup.string()
    .required("El país es obligatorio"),
  state: Yup.string()
    .required("El estado es obligatorio"),
  city: Yup.string()
    .required("La ciudad es obligatoria"),
  zipCode: Yup.string()
    .required("El código postal es obligatorio"),
  street: Yup.string()
    .required("La calle/dirección es obligatoria"),
  rfc: Yup.string()
    .required("El RFC es obligatorio"),
  phone: Yup.string()
    .required("El teléfono es obligatorio"),
  whatsappNumber: Yup.string()
    .required("El número de WhatsApp es obligatorio"),
  logo: Yup.mixed()
    .nullable()
    .notRequired()
    .test(
      "fileType",
      "Solo se permiten imágenes (jpg, jpeg, png, webp)",
      (value) => {
        if (!value) return true;
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
        return allowedTypes.includes(value.type);
      }
    ),
  password: Yup.string()
    .min(5, "¡Demasiado corto!")
    .max(50, "¡Demasiado largo!")
    .required("La contraseña es obligatoria"),
  email: Yup.string()
    .email("Email inválido")
    .required("El email de usuario es obligatorio"),
	isDoctor: Yup.boolean(), 
	latitude: Yup.string().required("Debe seleccionar una ubicación en el mapa"),
  longitude: Yup.string().required("Debe seleccionar una ubicación en el mapa"),
});


const SignUp = () => {
	useTitle("Registrarse");
	const classes = useStyles();
	const history = useHistory();

	const initialState = {
    name: "",
    email: "",
    password: "",
    companyName: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    street: "",
    rfc: "",
    phone: "",
    whatsappNumber: "",
    logo: null,
	isDoctor: false,
    latitude: "",
    longitude: "",
  };

	const [showPassword, setShowPassword] = useState(false);
	const [signUp] = useState(initialState);

	const handleSignUp = async (values) => {
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      formData.append("password", values.password);

      formData.append("companyName", values.companyName);
      formData.append("country", values.country);
      formData.append("state", values.state);
      formData.append("city", values.city);
      formData.append("zipCode", values.zipCode);
      formData.append("street", values.street);
      formData.append("rfc", values.rfc);
      formData.append("phone", values.phone);
      formData.append("whatsappNumber", values.whatsappNumber);
	  	formData.append("isDoctor", values.isDoctor);
      formData.append("latitude", values.latitude);
      formData.append("longitude", values.longitude);

      if (values.logo instanceof File) {
        formData.append("logo", values.logo);
      }

      await api.post("/auth/signup", formData);

      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

	return (
		<Container component="main" maxWidth="sm">
      <CssBaseline />
      <div className={classes.paper}>
				<Avatar className={classes.avatar}>
					<LockOutlined />
				</Avatar>
				<Typography component="h1" variant="h5">
					{i18n.t("signup.title")}
				</Typography>
				<Formik
          initialValues={signUp}
          enableReinitialize={true}
          validationSchema={SignUpSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSignUp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form className={classes.form}>
              <Grid container spacing={2} alignItems="flex-start">
                <Typography variant="h6" gutterBottom >Información de la Compañía</Typography>
                {/* Nombre + Logo */}
                <Grid item xs={12} sm={8}>
                  <Field
                    as={TextField}
                    autoComplete="organization"
                    name="companyName"
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                    variant="outlined"
                    fullWidth
                    label="Nombre de la Compañía"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={4} className={classes.logoContainer}>
                  <LogoUploader
                    onChange={(file) => {
                      setFieldValue("logo", file);
                    }}
                  />
                  {touched.logo && errors.logo && (
                    <div style={{ color: "red", fontSize: "0.8rem" }}>{errors.logo}</div>
                  )}
                </Grid>

                {/* Dirección */}
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="country"
                    error={touched.country && Boolean(errors.country)}
                    helperText={touched.country && errors.country}
                    variant="outlined"
                    fullWidth
                    label="País"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="state"
                    error={touched.state && Boolean(errors.state)}
                    helperText={touched.state && errors.state}
                    variant="outlined"
                    fullWidth
                    label="Estado"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="city"
                    error={touched.city && Boolean(errors.city)}
                    helperText={touched.city && errors.city}
                    variant="outlined"
                    fullWidth
                    label="Ciudad"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="zipCode"
                    error={touched.zipCode && Boolean(errors.zipCode)}
                    helperText={touched.zipCode && errors.zipCode}
                    variant="outlined"
                    fullWidth
                    label="Código Postal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="street"
                    error={touched.street && Boolean(errors.street)}
                    helperText={touched.street && errors.street}
                    variant="outlined"
                    fullWidth
                    label="Calle/Dirección"
                  />
                </Grid>
                
                {/* MAPA DE UBICACIÓN */}
                <Grid item xs={12}>
                   <LocationSelector 
                     country={values.country}
                     state={values.state}
                     city={values.city}
                     street={values.street}
                     onLocationChange={(lat, lng) => {
                        setFieldValue("latitude", lat);
                        setFieldValue("longitude", lng);
                     }}
                   />
									{touched.latitude && errors.latitude && (
                    <FormHelperText error style={{ marginTop: 8, textAlign: "center" }}>
                    	⚠️ {errors.latitude}
                    </FormHelperText>
                  )}
                </Grid>

                {/* RFC */}
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="rfc"
                    error={touched.rfc && Boolean(errors.rfc)}
                    helperText={touched.rfc && errors.rfc}
                    variant="outlined"
                    fullWidth
                    label="RFC"
                  />
                </Grid>

                {/* Teléfonos juntos */}
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="phone"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    variant="outlined"
                    fullWidth
                    label="Teléfono"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="whatsappNumber"
                    error={touched.whatsappNumber && Boolean(errors.whatsappNumber)}
                    helperText={touched.whatsappNumber && errors.whatsappNumber}
                    variant="outlined"
                    fullWidth
                    label="Número de WhatsApp"
                  />
                </Grid>

                {/* Usuario Admin */}
                <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>Usuario Administrador</Typography>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    autoComplete="name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    fullWidth
                    label={i18n.t("signup.form.name")}
                  />
                </Grid>
								<Grid item xs={12}>
                  <FormControl>
										<FormControlLabel
												control={ <Field as={Checkbox} name="isDoctor" color="primary" /> }
												label="Asignar también rol de Doctor"
										/>
										<FormHelperText>
												Permitirá a este usuario acceder a las herramientas de doctor (calendario, recetas, etc.).
										</FormHelperText>
									</FormControl>
                </Grid>
								<Grid item xs={12}>
									<Collapse in={values.isDoctor}>
										<Paper className={classes.alertBox} elevation={0}>
											<Info style={{ color: '#f57f17' }} />
											<Typography variant="caption">
												<strong>Importante:</strong> Después de registrarte, ve a la sección "Usuarios", 
												edita tu perfil y completa tus especialidades y horarios para aparecer en el calendario.
											</Typography>
										</Paper>
									</Collapse>
								</Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="email"
                    label={i18n.t("signup.form.email")}
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    autoComplete="email"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    name="password"
                    id="password"
                    autoComplete="current-password"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    label={i18n.t("signup.form.password")}
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((e) => !e)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Button type="submit" fullWidth variant="contained" color="primary" className={classes.submit}>
                {i18n.t("signup.buttons.submit")}
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Link href="#" variant="body2" component={RouterLink} to="/login">
                    {i18n.t("signup.buttons.login")}
                  </Link>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
			</div>
			<Box mt={5}>{/* <Copyright /> */}</Box>
		</Container>
	);
};

export default SignUp;